import { alpha } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import { type CSSProperties, type FC, type ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { ArrowContainer, type PopoverAlign, type PopoverPosition, type PopoverProps, type PopoverState, Popover as TinyPopover } from "react-tiny-popover";
import { INK_BLACK, SURFACE } from "../../../theme/tokens";
import { isInsidePortalOverlay } from "./portalOverlay";

function originFrom(position: PopoverPosition | undefined, align: PopoverAlign | undefined) {
	switch (position) {
		case "top":
			return align === "start" ? "left bottom" : align === "end" ? "right bottom" : "center bottom";
		case "bottom":
			return align === "start" ? "left top" : align === "end" ? "right top" : "center top";
		case "left":
			return align === "start" ? "right top" : align === "end" ? "right bottom" : "right center";
		case "right":
			return align === "start" ? "left top" : align === "end" ? "left bottom" : "left center";
		default:
			return "center center";
	}
}

function enterOffset(position?: PopoverPosition) {
	// subtle slide from the anchor side
	switch (position) {
		case "top":
			return { x: 0, y: 8 };
		case "bottom":
			return { x: 0, y: -8 };
		case "left":
			return { x: 8, y: 0 };
		case "right":
			return { x: -8, y: 0 };
		default:
			return { x: 0, y: 0 };
	}
}

/** Fully controlled — the caller owns open/close state (click, external trigger, …). */
type ControlledOpenProps = { isOpen: boolean; enterDelay?: undefined; leaveDelay?: undefined; disabledChildrenWrapper?: boolean; onOpenChange?: undefined };
/**
 * Hover-triggered — the popover owns its own open state, opening/closing on a delay via
 * `onMouseEnter`/`onMouseLeave` (and focus equivalents) attached to the wrapper div around
 * `children`, and to the popover body — so focus moved INTO the body (Tab / a caller's arrow-key
 * handoff to a link inside) keeps it open like a pointer over it does. `disabledChildrenWrapper`
 * removes that wrapper, so it can't be combined with hover mode — there would be nothing left to
 * attach the open/close listeners to.
 */
type HoverOpenProps = {
	isOpen?: undefined;
	enterDelay: number;
	leaveDelay: number;
	disabledChildrenWrapper?: false;
	/** Hover mode owns the open state; this reports it (e.g. for the trigger's `aria-expanded`, or analytics). */
	onOpenChange?: (open: boolean) => void;
};

export type SystemReactTinyPopoverProps = (ControlledOpenProps | HoverOpenProps) & {
	onClose?: () => void;
	/** Fires after the exit animation fully completes — lets a parent unmount the wrapper only once the close has played out. */
	onExitComplete?: () => void;
	contentRootStyle?: CSSProperties;
	content: ReactElement;
	arrowOpacity?: CSSProperties["opacity"];
	arrowSize?: number;
	/**
	 * For a popover hosting a whole FORM rather than a short menu: cap it to the room on the roomier of
	 * the sides it may open to (above or below the trigger) and scroll its own body, so the buttons at
	 * the form's foot (Save, Cancel) stay reachable on a short screen. `reposition` alone cannot do this —
	 * it picks the better side, but once neither side has room for the full content it still overflows
	 * off-screen. No effect on a popover that already fits.
	 */
	fitViewport?: boolean;
	/**
	 * Close on `Escape` while open. Opt-in rather than always-on: a popover nested inside another
	 * overlay would otherwise have both layers react to the same keypress, and existing callers that
	 * already bind their own handler must keep behaving exactly as they do. Turn it on for any popover
	 * a keyboard user can open — dismissing it should not require finding somewhere else to click.
	 */
	closeOnEscape?: boolean;
	closeOnOutOfViewport?: boolean;
	childrenWrapperStyle?: CSSProperties;
	"data-testid"?: string;
} & Omit<PopoverProps, "content" | "isOpen">;

/** The popover root's inner padding — shared so the fit-to-viewport maths can discount it. */
const POPOVER_ROOT_PADDING = 12;

export const SystemReactTinyPopoverRootStyle: CSSProperties = {
	background: SURFACE,
	borderRadius: 12,
	boxShadow: `0 10px 30px ${alpha(INK_BLACK, 0.12)}`,
	padding: POPOVER_ROOT_PADDING,
	minWidth: 180,
} as const satisfies CSSProperties;

export const SystemReactTinyPopoverChildrenWrapperStyle: CSSProperties = {
	width: "fit-content",
	height: "fit-content",
} as const satisfies CSSProperties;

/** Gap kept between a viewport-clamped popover and the edge of the screen. */
export const SYSTEM_POPOVER_VIEWPORT_INSET = 12;

/**
 * The default `arrowSize`. The arrow's container pads the popover by it on every side (invisible arrow or not), so a
 * popover capping its own width to the viewport subtracts it twice as well as the inset.
 */
export const SYSTEM_POPOVER_ARROW_SIZE = 8;

/** `react-tiny-popover`'s `positions` when none are passed. */
const TINY_POPOVER_DEFAULT_POSITIONS: readonly PopoverPosition[] = ["top", "left", "right", "bottom"];

/** Below this a scrollable popover is too cramped to be worth showing in that direction. */
const MIN_FIT_HEIGHT = 160;

/**
 * Published on the popover root under `fitViewport` so content that imposes its OWN height cap can
 * defer to the room actually available — `min(80vh, var(--system-popover-available-height, 80vh))`.
 * A viewport-relative cap inside a popover is wrong whenever the popover has less than that: the
 * content then scrolls inside a box taller than its container, putting its foot past the clip edge
 * where no amount of scrolling the popover reaches it. The fallback keeps it a no-op elsewhere.
 */
export const SYSTEM_POPOVER_AVAILABLE_HEIGHT_VAR = "--system-popover-available-height";

/** What the room around the trigger is measured against: the popover's own extent beyond its root's `max-height`. */
type FitGeometry = {
	childRect: PopoverState["childRect"];
	/** The arrow container pads the popover by this on every side. */
	arrowSize: number;
	/** The gap the library keeps between trigger and popover (`padding`). */
	padding: number;
	/** The gap kept to the screen edge. */
	inset: number;
	/** The root's own vertical padding when `max-height` doesn't include it (a content-box root); 0 otherwise. */
	rootPadding: number;
};

/** The tallest root that still fits on that side by the library's own test, so it is placed there rather than flipped. */
const roomOn = (side: "top" | "bottom", { childRect, arrowSize, padding, inset, rootPadding }: FitGeometry): number => {
	const beyondRoot = padding + inset + arrowSize * 2 + rootPadding;
	return side === "top" ? childRect.top - beyondRoot : window.innerHeight - childRect.bottom - beyondRoot;
};

/**
 * How much room the popover actually has. This — not the viewport height — is the limit that matters: a form 599px
 * tall in a 738px viewport "fits the screen" and still has its Save button cut off, because only the 578px below the
 * trigger is available to it.
 *
 * Above or below, it is the roomier of the vertical sides the popover may open to, whichever it is on now: a cap that
 * followed the current side could fit only the OTHER side, so content taller than both flipped sides on every render
 * until React gave up ("Maximum update depth exceeded"). The library places it on the side that fits that cap.
 */
const availableHeight = (position: PopoverPosition | undefined, positions: readonly PopoverPosition[], geometry: FitGeometry): number => {
	if (position === "top" || position === "bottom") {
		const sides = positions.filter((side): side is "top" | "bottom" => side === "top" || side === "bottom");
		return Math.max(MIN_FIT_HEIGHT, ...sides.map((side) => roomOn(side, geometry)));
	}
	// Side-anchored: the popover is vertically centred on the trigger, so the whole column is fair game.
	return Math.max(MIN_FIT_HEIGHT, window.innerHeight - geometry.inset * 2);
};

export const SystemReactTinyPopover: FC<SystemReactTinyPopoverProps> = ({
	onClose,
	onExitComplete,
	isOpen,
	enterDelay,
	leaveDelay,
	children,
	content,
	arrowSize = SYSTEM_POPOVER_ARROW_SIZE,
	arrowOpacity = 0,
	contentRootStyle = SystemReactTinyPopoverRootStyle,
	fitViewport = false,
	closeOnEscape = false,
	closeOnOutOfViewport = true,
	childrenWrapperStyle = SystemReactTinyPopoverChildrenWrapperStyle,
	onClickOutside,
	disabledChildrenWrapper = false,
	onOpenChange,
	...rest
}) => {
	// Hover mode is opted into by passing enterDelay/leaveDelay instead of isOpen (see the
	// discriminated union above) — controlled callers never pass these, so they stay on the
	// isOpen branch below, byte-for-byte unaffected.
	const isHoverMode = enterDelay !== undefined && leaveDelay !== undefined;
	const [hoverOpen, setHoverOpen] = useState(false);
	const effectiveOpen = isHoverMode ? hoverOpen : Boolean(isOpen);

	// Reported on change only: the first run (closed, as mounted) is not a change.
	const reportedOpenRef = useRef(false);
	useEffect(() => {
		if (reportedOpenRef.current === hoverOpen) return;
		reportedOpenRef.current = hoverOpen;
		onOpenChange?.(hoverOpen);
	}, [hoverOpen, onOpenChange]);

	const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearOpenTimer = useCallback(() => {
		if (openTimerRef.current) {
			clearTimeout(openTimerRef.current);
			openTimerRef.current = null;
		}
	}, []);
	const clearCloseTimer = useCallback(() => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	}, []);
	useEffect(
		() => () => {
			clearOpenTimer();
			clearCloseTimer();
		},
		[clearOpenTimer, clearCloseTimer],
	);

	// Enter/leave handlers are attached to both the trigger (children) wrapper and the rendered
	// popover content root, so moving the pointer from the trigger into the popover body (e.g. to
	// scroll a list) doesn't let the leave timer close it. No-op outside hover mode.
	const handleHoverEnter = useCallback(() => {
		if (!isHoverMode) return;
		clearCloseTimer();
		if (hoverOpen) return;
		openTimerRef.current = setTimeout(() => {
			openTimerRef.current = null;
			setHoverOpen(true);
		}, enterDelay);
	}, [isHoverMode, hoverOpen, enterDelay, clearCloseTimer]);
	const handleHoverLeave = useCallback(() => {
		if (!isHoverMode) return;
		clearOpenTimer();
		if (!hoverOpen) return;
		closeTimerRef.current = setTimeout(() => {
			closeTimerRef.current = null;
			setHoverOpen(false);
		}, leaveDelay);
	}, [isHoverMode, hoverOpen, leaveDelay, clearOpenTimer]);

	// The fit maths reads the placement props the library gets through `rest`.
	// DEFAULT: the library's own defaults when they aren't passed (every side; no gap; no inset).
	const passedPositions = rest.positions ?? TINY_POPOVER_DEFAULT_POSITIONS;
	const fitPositions = typeof passedPositions === "string" ? [passedPositions] : passedPositions;
	// DEFAULT: as above.
	const fitPadding = rest.padding ?? 0;
	// The screen-edge gap is at least the house inset, as it always was here; a larger one the library keeps too.
	// DEFAULT: as above.
	const fitInset = Math.max(SYSTEM_POPOVER_VIEWPORT_INSET, rest.boundaryInset ?? 0);
	// A content-box root grows by its own padding beyond `max-height`. Only a numeric padding can be read here; any other
	// is left out, which can't make the popover flip sides (the cap no longer depends on the side), only overflow by it.
	const rootPadding = contentRootStyle.boxSizing !== "border-box" && typeof contentRootStyle.padding === "number" ? contentRootStyle.padding * 2 : 0;

	const [animationState, setAnimationState] = useState<{
		open: boolean;
		isExiting: boolean;
	}>({ open: false, isExiting: false });
	const showPopover = animationState.open || animationState.isExiting;

	const childrenDivRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		setAnimationState((prev) => (effectiveOpen ? { open: true, isExiting: false } : { open: false, isExiting: prev.open }));
	}, [effectiveOpen]);

	const closeWithExit = useCallback(() => {
		if (!showPopover) {
			return;
		}
		onClose?.();
	}, [onClose, showPopover]);

	// react-tiny-popover flags any click whose target isn't inside the popover's own DOM subtree as
	// "outside" — but a nested surface that portals to <body> (SystemDropdown's panel, a mobile sheet)
	// is outside by DOM only, not by intent. Swallow those so picking an option inside a nested
	// dropdown can't tear this popover down before the user commits (e.g. Edit Shift Details → Save).
	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (isInsidePortalOverlay(event.target)) {
				return;
			}
			// Hover mode closes immediately on an outside click, bypassing the leave delay.
			if (isHoverMode) {
				clearOpenTimer();
				clearCloseTimer();
				setHoverOpen(false);
			}
			if (onClickOutside) {
				onClickOutside(event);
				return;
			}
			closeWithExit();
		},
		[onClickOutside, closeWithExit, isHoverMode, clearOpenTimer, clearCloseTimer],
	);

	useEffect(() => {
		if (!closeOnEscape || !showPopover) {
			return;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			// Same exemption as an outside click: a nested surface that portals to <body> (a dropdown's
			// panel, a mobile sheet) owns the first Escape — closing this popover out from under it would
			// tear down the thing the user was actually dismissing.
			if (event.key !== "Escape" || isInsidePortalOverlay(event.target)) {
				return;
			}
			if (isHoverMode) {
				clearOpenTimer();
				clearCloseTimer();
				setHoverOpen(false);
				return;
			}
			closeWithExit();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [closeOnEscape, showPopover, isHoverMode, clearOpenTimer, clearCloseTimer, closeWithExit]);

	return (
		<TinyPopover
			isOpen={showPopover}
			onClickOutside={handleClickOutside}
			reposition={true}
			content={(state: PopoverState) => {
				const { position, align, popoverRect, childRect } = state;
				const fitHeight = fitViewport
					? availableHeight(position, fitPositions, { childRect, arrowSize, padding: fitPadding, inset: fitInset, rootPadding })
					: 0;
				// Asserted because `CSSProperties` has no slot for a CSS custom property.
				const rootStyle: CSSProperties = fitViewport
					? ({
							...contentRootStyle,
							maxHeight: fitHeight,
							overflowY: "auto",
							// Stops a scroll that reaches the popover's end from chaining to the page behind it.
							overscrollBehavior: "contain",
							// Discount the root's own padding so content sizing against this variable lands
							// inside the box rather than flush with its edge.
							[SYSTEM_POPOVER_AVAILABLE_HEIGHT_VAR]: `${Math.max(MIN_FIT_HEIGHT, fitHeight - POPOVER_ROOT_PADDING * 2)}px`,
						} as CSSProperties)
					: contentRootStyle;
				return (
					<AnimatePresence
						onExitComplete={() => {
							setAnimationState({ open: false, isExiting: false });
							onExitComplete?.();
						}}
					>
						{animationState.open && (
							<motion.div
								initial={{ opacity: 0, scale: 0.96, ...enterOffset(position) }}
								animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
								exit={{ opacity: 0, scale: 0.98 }}
								transition={{
									type: "spring",
									stiffness: 420,
									damping: 28,
									mass: 0.7,
								}}
								style={{ transformOrigin: originFrom(position, align) }}
							>
								<ArrowContainer
									position={position}
									childRect={childRect}
									popoverRect={popoverRect}
									arrowSize={arrowSize}
									arrowColor={SURFACE}
									arrowStyle={{ opacity: arrowOpacity }}
								>
									{/* biome-ignore lint/a11y/noStaticElementInteractions: pointer/focus-only hover-delay tracking wrapper in optional hover mode — the popover's actual interactive content lives inside `content` and carries its own semantics/roles. */}
									<div
										data-testid={rest["data-testid"]}
										style={rootStyle}
										onMouseEnter={isHoverMode ? handleHoverEnter : undefined}
										onMouseLeave={isHoverMode ? handleHoverLeave : undefined}
										onFocus={isHoverMode ? handleHoverEnter : undefined}
										onBlur={isHoverMode ? handleHoverLeave : undefined}
									>
										{content}
									</div>
								</ArrowContainer>
							</motion.div>
						)}
					</AnimatePresence>
				);
			}}
			{...rest}
		>
			{disabledChildrenWrapper ? (
				children
			) : (
				// biome-ignore lint/a11y/noStaticElementInteractions: pointer/focus-only hover-delay tracking wrapper in optional hover mode — `children` (the trigger) carries its own semantics/roles.
				<div
					ref={childrenDivRef}
					style={childrenWrapperStyle}
					onMouseEnter={isHoverMode ? handleHoverEnter : undefined}
					onMouseLeave={isHoverMode ? handleHoverLeave : undefined}
					onFocus={isHoverMode ? handleHoverEnter : undefined}
					onBlur={isHoverMode ? handleHoverLeave : undefined}
				>
					{children}
				</div>
			)}
		</TinyPopover>
	);
};
